<template>
  <div v-if="hasCoupons" class="discountBanner" :class="{compact, heightAdaptive, fullOnMobile, rounded: !tile, 'rounded-0': tile}">
    <div ref="progress" :class="{discountProgress: true, active: isOpen}"
         role="button"
         tabindex="0"
         :aria-expanded="isOpen ? 'true' : 'false'"
         :aria-label="$t('COUPON_TITLE')"
         :aria-haspopup="mobile ? 'dialog' : undefined"
         @click="toggleOpen()"
         @keydown.enter.prevent="open = !open"
         @keydown.space.prevent="open = !open"
         @keydown.esc.prevent="close()"
         @focusout="onFocusOut()"
         @mouseover="onHover(true)"
         @mouseleave="onHover(false)"
    >
      <div class="bannerHeader">
        <span class="stage"><span class="stageIcon secondary" aria-hidden="true">%</span>{{ $t('COUPON_STAGE').replace('{current}', nextCouponIndex).replace('{total}', discountCoupons.length) }}</span>
        <span v-if="activeCoupon" class="activeReward rewardStatus">
          <v-icon size="16" class="rewardCheck">check</v-icon>
          {{ $t('COUPON_REWARD_ACTIVE').replace('{reward}', rewardLabel(activeCoupon)) }}
        </span>
      </div>
      <div class="progressTrack">
        <span class="discount" :style="{left: `${discountPercentage}%`}" aria-hidden="true" v-html="discountSvg"></span>
        <div class="meter">
          <span class="secondary progress" :style="{width: `${discountPercentage}%`}"></span>
          <span v-for="(coupon, index) in discountCoupons" :key="index" v-if="coupon.minimumValue > 0 && coupon.minimumValue < maxGoal"
                class="marker" :style="{'left': `${percentageOf(coupon.minimumValue)}%`}"></span>
          <div v-if="!mobile && !detachedDetails" class="secondary tooltip" v-show="isOpen">
            <DiscountDetails :coupons="coupons" :subtotal="subtotal" :next-coupon="nextCoupon" :expiry-date="expiryDate"/>
          </div>
        </div>
      </div>
      <div class="bannerSummary" :class="{centeredOnMobile: !activeCoupon}">
        <span v-if="(!mobile || fullOnMobile) && freeShippingCoupon && (hasNextCoupon || !hasPendingShippingReward)" class="freeShipping" :class="{rewardStatus: couponActive(freeShippingCoupon)}">
          <template v-if="couponActive(freeShippingCoupon)">
            <v-icon size="16" class="rewardCheck">check</v-icon>
            {{ $t('COUPON_REWARD_SHIPPING') }}
          </template>
          <template v-else>
            {{ $t('COUPON_SHIPPING_FROM').replace('{value}', formatPrice(freeShippingCoupon.minimumValue)) }}
          </template>
        </span>
        <i18n v-if="nextRewardCoupon" path="COUPON_NEXT_REWARD" tag="p" class="missing">
          <strong place="value" class="missingValue">{{ formatPrice(nextRewardCoupon.minimumValue - subtotal) }}</strong>
          <strong place="reward" class="nextReward">{{ rewardLabel(nextRewardCoupon) }}</strong>
        </i18n>
        <p v-else class="missing allActivated">
          {{ $t('COUPON_ALL_ACTIVATED') }}
        </p>
      </div>
    </div>
    <v-menu v-if="!mobile && detachedDetails" v-model="open" :activator="desktopActivator"
            open-on-hover :open-on-click="false" :close-on-content-click="false" :close-delay="150"
            offset-y :nudge-right="desktopPopoverOffset" max-width="350" max-height="calc(100vh - 32px)" content-class="discountDetailsPopover">
      <DiscountDetails :coupons="coupons" :subtotal="subtotal" :next-coupon="nextCoupon" :expiry-date="expiryDate"/>
    </v-menu>
    <v-dialog v-if="mobile" v-model="open" max-width="350" content-class="discountDetailsDialog"
              :aria-label="$t('COUPON_TITLE')">
      <DiscountDetails :coupons="coupons" :subtotal="subtotal" :next-coupon="nextCoupon"
                       :expiry-date="expiryDate" modal @close="close()"/>
    </v-dialog>
  </div>

</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {BasketDto, CouponDto, CouponTypeDto} from "@swagger";
import {Prop, Watch} from 'vue-property-decorator';
import DiscountDetails from '@/components/DiscountDetails.vue';
import discountSvg from '@/assets/coupons/discount.svg?raw';

const MOBILE_DISCOUNT_DETAILS_SEEN_KEY = 'shop.mobileDiscountDetailsSeen';
let mobileDiscountDetailsShown = false;

@Component({
  components: {DiscountDetails}
})
export default class DiscountBanner extends BasicComponent {

  discountSvg = discountSvg;

  @Prop({type: Boolean, default: false}) compact: boolean;
  @Prop({type: Boolean, default: false}) fullOnMobile: boolean;
  @Prop({type: Boolean, default: false}) detachedDetails: boolean;
  @Prop({type: Boolean, default: false}) heightAdaptive: boolean;
  @Prop({type: Boolean, default: false}) tile: boolean;
  @Prop({type: Boolean, default: false}) autoOpenOnMobile: boolean;

  open: boolean = false;
  hover: boolean = false;
  desktopActivator: HTMLElement | null = null;
  desktopActivatorWidth = 0;
  activatorObserver: ResizeObserver | null = null;

  get desktopPopoverOffset(): number {
    return Math.max(0, (this.desktopActivatorWidth - Math.min(350, this.desktopActivatorWidth)) / 2);
  }

  syncDesktopActivator() {
    const element = this.$refs.progress as HTMLElement | undefined;
    const shouldObserve = !!element && this.detachedDetails && !this.mobile;
    if (element === this.desktopActivator && shouldObserve === !!this.activatorObserver) {
      return;
    }
    this.activatorObserver?.disconnect();
    this.activatorObserver = null;
    this.desktopActivator = element ?? null;
    if (!shouldObserve) {
      return;
    }
    this.desktopActivatorWidth = element.getBoundingClientRect().width;
    // Detached Vuetify menu centering depends on the activator's actual layout width.
    this.activatorObserver = new ResizeObserver(() => {
      this.desktopActivatorWidth = element.getBoundingClientRect().width;
    });
    this.activatorObserver.observe(element);
  }

  updated() {
    this.syncDesktopActivator();
  }

  beforeDestroy() {
    this.activatorObserver?.disconnect();
  }


  get shouldAutoOpen(): boolean {
    return this.autoOpenOnMobile && this.mobile && this.hasCoupons
        && this.$route.name === 'home' && Number(this.$store.getters.step) === 3;
  }

  mounted() {
    this.syncDesktopActivator();
    this.maybeAutoOpen();
  }

  @Watch('shouldAutoOpen')
  onAutoOpenEligibilityChange() {
    this.$nextTick(this.maybeAutoOpen);
  }

  maybeAutoOpen() {
    if (!this.shouldAutoOpen || mobileDiscountDetailsShown) {
      return;
    }
    try {
      if (localStorage.getItem(MOBILE_DISCOUNT_DETAILS_SEEN_KEY) === '1') {
        return;
      }
    } catch {
      // If storage is unavailable, still show the introduction once in this session.
    }
    mobileDiscountDetailsShown = true;
    this.open = true;
    try {
      localStorage.setItem(MOBILE_DISCOUNT_DETAILS_SEEN_KEY, '1');
    } catch {
      // The in-memory flag prevents repeated openings until the page is reloaded.
    }
  }

  @Watch('mobile')
  onBreakpointChange() {
    this.close();
  }

  onFocusOut() {
    if (!this.mobile && !this.detachedDetails) {
      this.close();
    }
  }

  get coupons(): Array<CouponDto> {
    return this.$store.getters.coupons;
  }


  get discountCoupons(): Array<CouponDto> {
    return this.coupons.filter(coupon => coupon.type !== CouponTypeDto.FREESHIPPING);
  }

  get nextCouponIndex() {
    let i = 0;
    for (; i < this.discountCoupons.length; i++) {
      if (this.discountCoupons[i].minimumValue > this.subtotal) {
        break;
      }
    }
    return i;
  }

  get hasNextCoupon() {
    return this.nextCouponIndex < this.discountCoupons.length;
  }

  get nextCoupon() {
    if (!this.hasNextCoupon) {
      return null;
    }
    return this.discountCoupons[this.nextCouponIndex];
  }

  get nextRewardCoupon(): CouponDto | null {
    return this.nextCoupon || (this.hasPendingShippingReward ? this.freeShippingCoupon : null);
  }

  get activeCoupon(): CouponDto | null {
    const activeDiscounts = this.discountCoupons.filter(coupon => this.couponActive(coupon));
    return activeDiscounts.reduce((best, coupon) => {
      const value = (reward: CouponDto) => reward.type === CouponTypeDto.PERCENTAGE ? this.subtotal * reward.discount / 100 : reward.discount;
      return !best || value(coupon) > value(best) ? coupon : best;
    }, null as CouponDto | null);
  }

  get freeShippingCoupon(): CouponDto | undefined {
    return this.coupons
        .filter(coupon => coupon.type === CouponTypeDto.FREESHIPPING)
        .reduce((best, coupon) => !best || coupon.minimumValue < best.minimumValue ? coupon : best, undefined as CouponDto | undefined);
  }

  get hasPendingShippingReward(): boolean {
    return !!this.freeShippingCoupon && !this.couponActive(this.freeShippingCoupon);
  }

  rewardLabel(coupon: CouponDto): string {
    if (coupon.type === CouponTypeDto.FREESHIPPING) {
      return this.$t('COUPON_REWARD_SHIPPING') as string;
    }
    const discount = coupon.type === CouponTypeDto.PERCENTAGE ? `${coupon.discount}%` : this.formatPrice(coupon.discount);
    return (this.$t('COUPON_REWARD_DISCOUNT') as string).replace('{discount}', discount);
  }

  get maxGoal(): number {
    if (!this.discountCoupons.length) {
      return 0.0;
    }
    return this.discountCoupons[this.discountCoupons.length - 1].minimumValue;
  }

  get basket(): BasketDto {
    return this.$store.getters.basket;
  }

  get subtotal(): number {
    const basket = this.basket;
    if (!basket) {
      return 0.0;
    }
    return basket.subtotal;
  }

  couponActive(coupon: CouponDto): boolean {
    return this.subtotal >= coupon.minimumValue;
  }

  percentageOf(value: number): number {
    if (this.maxGoal === 0.0) {
      return 0;
    }
    return Math.max(0, Math.min(100, value * 100 / this.maxGoal));
  }

  get discountPercentage(): number {
    return this.percentageOf(this.subtotal);
  }

  get expiryDate(): string {
    const dates = this.coupons.map(coupon => coupon.activeUntil).filter(el => el != null).sort();
    if (dates.length === 0) {
      return null;
    }
    return dates[0];
  }

  toggleOpen() {
    if (this.hover) {
      return;
    }
    this.open = !this.open;
  }

  close() {
    this.open = false;
    this.hover = false;
  }

  onHover(val) {
    if (val && !this.hover) {
      this.trackEvent("Gutschein", "Gutscheinliste geöffnet")
    }
    if (this.mobile || this.detachedDetails) {
      return;
    }
    this.hover = val;
  }

  get isOpen() {
    return this.open || this.hover;
  }

}

</script>
<style lang="scss" scoped>
@use "../stylesheets/Variables.scss" as *;


.discountBanner {
  margin: 0 0 0 auto;
  padding: 14px 16px;
  background: $shop-white;
  box-shadow: 0 2px 8px rgba($shop-black, .14);

  min-width: 100px;
  width: 640px;
  max-width: 100%;

  @media (max-width: 959px) {
    margin-inline: auto;
    padding-block: 8px;
  }

  .bannerHeader, .bannerSummary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 12px;
  }

  .bannerHeader {

    font-size: 14px;

    font-weight: 600;

  }
  .stage {
    display: inline-flex;
    align-items: center;
    gap: 7px;
  }
  .stageIcon {
    width: 21px;
    height: 21px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--v-accent-base);
    font-size: 11px;
  }
  .rewardStatus {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 7px;
    border-radius: 4px;
    color: var(--v-accent-base);
    background: color-mix(in srgb, var(--v-accent-base) 10%, $shop-white);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.5;

    .rewardCheck {

      color: inherit;

    }
  }
  .progressTrack {
    position: relative;
    padding-top: 18px;
    margin: 2px 0 8px;
  }
  .bannerSummary {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, auto);
    font-size: 17px;
    line-height: 1.5;
  }
  .freeShipping {
    grid-column: 1;
    grid-row: 1;
    font-size: 13px;
    justify-self: start;
    align-self: center;
  }
  .bannerSummary .missing {
    grid-column: 2;
    grid-row: 1;
  }
  .missingValue {
    color: $price-color-discount;
    white-space: nowrap;
  }
  .nextReward {
    color: var(--v-accent-base);
  }
  @media screen and (max-width: 390px) {
    padding-left: 10px;
    padding-right: 10px;
  }

  .discountProgress {

    $discountHeight: 22px;

    cursor: pointer;
    position: relative;
    border-radius: 3px;
    &:focus-visible {
      outline: 2px solid var(--v-accent-base);
      outline-offset: 6px;
    }
    width: 100%;

    .discount {
      position: absolute;
      width: 16px;
      height: $discountHeight;
      z-index: 51;
      display: block;
      top: 0;
      pointer-events: none;
      transform: translateX(-50%);
      transition: left .3s ease;

      ::v-deep svg {
        display: block;
        width: 100%;
        height: 100%;
      }

      ::v-deep .foreground {
        fill: $shop-white;
      }

      ::v-deep .background {
        fill: currentColor;
      }
    }

    .progress {
      font-weight: 500;
      text-align: center;
      margin-bottom: 2px;
    }

    .missing {
      text-align: right;
      font-size: inherit;
      margin: 0 0 0 auto;
      font-weight: 500;

    }

    .allActivated {
      color: var(--v-accent-base);
      font-weight: 400;
      .v-icon {
        color: inherit;
      }
    }

    $meterHeight: 8px;

    .meter {
      height: $meterHeight;
      width: 100%;
      position: relative;
      background: $shop-progress-track;
      -moz-border-radius: 25px;
      -webkit-border-radius: 25px;
      border-radius: 25px;
      box-shadow: inset 0 -1px 1px rgba($shop-white, 0.3);

      .progress {
        display: block;
        height: 100%;
        border-radius: 20px;
        min-width: 0;
        transition: width .3s ease;

        box-shadow: none;
        position: relative;
        overflow: hidden;
        content: "";
      }

      .marker {
        height: $meterHeight;
        width: 2px;
        background-color: $shop-white;
        transform: translateX(-50%);
        z-index: 50;
        display: block;
        position: absolute;
        top: 0;

      }

      .tooltip {
        $borderWidth: 3px;
        $pointerSize: 20px;

        visibility: hidden;
        width: 350px;
        max-width: 100%;

        top: 30px;
        right: 50%;
        transform: translateX(50%);

        background-color: $shop-white !important;
        border: $borderWidth solid var(--v-secondary-base);
        border-radius: 6px;
        box-shadow: 0 6px 20px rgba($shop-black, .12);
        color: $shop-black;
        padding: 5px;
        position: absolute;
        // Coupon details must remain above the fixed basket dock (120).
        z-index: 130;

        &::before {
          content: "";
          position: absolute;
          border-style: solid;
          border-color: inherit;
          border-left-color: transparent;
          border-right-color: transparent;
          display: block;
          width: 0;
          top: calc(-#{$pointerSize});
          bottom: auto;
          left: auto;
          right: 50%;
          transform: translateX(50%);
          border-width: 0 $pointerSize $pointerSize;
        }

        &::after {
          content: "";
          position: absolute;
          border-style: solid;
          border-color: $shop-white transparent;
          display: block;
          width: 0;
          $offset: #{$pointerSize} - #{$borderWidth} - 2px;
          top: calc(0px - (#{$offset}));
          bottom: auto;
          right: 50%;
          transform: translateX(50%);
          border-width: 0 calc(#{$offset}) calc(#{$offset});
        }


      }
    }

    &.active {
      .tooltip {
        visibility: visible;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .discount, .meter .progress {
        transition: none;
      }
    }
  }

  &.compact {
    padding: 6px 12px;
    .discountProgress {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, auto);
      align-items: center;
      gap: 6px 8px;
    }
    .bannerHeader, .bannerSummary {
      display: contents;
    }
    .stage, .freeShipping {
      display: none;
    }
    .activeReward {
      grid-column: 1;
      grid-row: 2;
      justify-self: start;
      margin-inline: 0;
    }
    .discountProgress .discount {
      display: none;
    }
    .progressTrack {
      grid-column: 1 / -1;
      grid-row: 1;
      padding-top: 0;
      margin: 0;
    }
    .discountProgress .missing {
      grid-column: 2;
      grid-row: 2;
      min-width: 0;
      font-size: 16px;
      line-height: 1.4;
    }
  }

  @media (max-width: 959px) {
    &:not(.fullOnMobile) {
      padding-inline: 8px;
      .discountProgress {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
      }
    .bannerHeader, .bannerSummary {
      display: contents;
    }
    .stage, .freeShipping {
      display: none;
    }
    .activeReward {
      order: 1;
      flex: 0 0 auto;
      margin-inline: auto;
      white-space: nowrap;
    }
    .progressTrack {
      order: 0;
      flex: 0 0 100%;
    }
    &:not(.compact) {
      .discountProgress {
        gap: 8px;
      }
      .discountProgress .missing {
        font-size: 16px;
      }
    }
    .discountProgress .missing {
      order: 2;
      flex: 0 0 auto;
      margin-inline: auto;
      text-align: center;
      white-space: nowrap;
      }
      .bannerSummary.centeredOnMobile .missing {
        grid-column: 1 / -1;
        text-align: center;
        margin-inline: 0;
      }
    }
  }

}

// Keep all reward information available while giving short configurators more room.
@media (max-height: 650px) {
  .discountBanner.heightAdaptive {
    padding-block: 4px;
    .bannerHeader, .bannerSummary {
      gap: 3px 8px;
    }
    .bannerSummary {
      font-size: 14px;
      line-height: 1.3;
    }
    .rewardStatus {
      padding-block: 1px;
    }
    .progressTrack {
      padding-top: 10px;
      margin: 1px 0 4px;
    }
    .discountProgress .discount {
      height: 14px;
    }
    &.compact {
      .discountProgress {
        gap: 3px 8px;
      }
      .progressTrack {
        padding-top: 0;
        margin: 0;
      }
      .discountProgress .missing {
        font-size: 14px;
        line-height: 1.3;
      }
    }
  }
}
</style>
<style lang="scss">
.discountDetailsPopover {
  padding: 5px;
  background: white;
  border: 3px solid var(--v-secondary-base);
  border-radius: 6px;
}

.v-dialog.discountDetailsDialog:not(.v-dialog--fullscreen) {
  margin: 16px;
  max-height: calc(100dvh - 32px);
}
</style>
